import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const TABS = [
  ["training", "Training pathways"],
  ["jobs", "Role profiles"],
];

const Chip = ({ active, children, ...rest }) => (
  <button
    type="button"
    className={`px-4 py-2 text-[0.62rem] font-bold uppercase tracking-[0.18em] transition-colors ${
      active ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"
    }`}
    {...rest}
  >
    {children}
  </button>
);

const TrainingRow = ({ item, index }) => (
  <div
    className="grid grid-cols-1 gap-4 border-b border-white/10 py-7 md:grid-cols-[64px_minmax(0,2fr)_minmax(0,1.6fr)_auto] md:gap-8"
    data-testid={`training-row-${item.slug}`}
  >
    <span className="mono text-sm text-maasai">{String(index + 1).padStart(2, "0")}</span>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-ochre">{item.track}</span>
        <span className="mono text-[0.62rem] text-zinc-500">{item.level}</span>
        {item.source && item.source !== "KickStartKenyaFootball" ? (
          <span
            className="border border-maasai px-2 py-[3px] text-[0.55rem] font-bold uppercase tracking-[0.16em] text-maasai"
            data-testid={`training-source-${item.slug}`}
          >
            {item.source}
          </span>
        ) : null}
      </div>
      <h3 className="mt-3 font-display text-xl uppercase leading-tight tracking-tight text-white md:text-2xl">
        {item.title}
      </h3>
      <p className="body-copy mt-3">{item.description}</p>
      {item.outcomes ? (
        <p className="mt-4 border-l-2 border-maasai pl-4 text-sm text-zinc-400">
          <span className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-zinc-400">You come away with</span>
          <br />
          {item.outcomes}
        </p>
      ) : null}
    </div>
    <dl className="mono grid grid-cols-2 gap-x-6 gap-y-3 text-xs text-zinc-400 md:grid-cols-1">
      {[
        ["Provider", item.provider],
        ["Where", item.location],
        ["Length", item.duration],
        ["Cost", item.cost],
      ]
        .filter(([, v]) => v)
        .map(([k, v]) => (
          <div key={k}>
            <dt className="text-[0.56rem] uppercase tracking-[0.22em] text-zinc-400">{k}</dt>
            <dd className="mt-1 text-zinc-300">{v}</dd>
          </div>
        ))}
    </dl>
    <div className="justify-self-start md:justify-self-end">
      <span
        data-testid={`training-cta-${item.slug}`}
        className="inline-flex cursor-not-allowed items-center gap-2 border border-white/15 px-5 py-3 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-zinc-500"
        title="Registration is not open yet"
      >
        Opens later
      </span>
    </div>
  </div>
);

const JobRow = ({ item, index }) => (
  <div
    className="grid grid-cols-1 gap-4 border-b border-white/10 py-7 md:grid-cols-[64px_minmax(0,2fr)_minmax(0,1.6fr)_auto] md:gap-8"
    data-testid={`job-row-${item.slug}`}
  >
    <span className="mono text-sm text-maasai">{String(index + 1).padStart(2, "0")}</span>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-ochre">{item.discipline}</span>
        <span className="mono text-[0.62rem] text-zinc-500">{item.contract}</span>
        {item.source && item.source !== "KickStartKenyaFootball" ? (
          <span
            className="border border-maasai px-2 py-[3px] text-[0.55rem] font-bold uppercase tracking-[0.16em] text-maasai"
            data-testid={`job-source-${item.slug}`}
          >
            {item.source}
          </span>
        ) : null}
      </div>
      <h3 className="mt-3 font-display text-xl uppercase leading-tight tracking-tight text-white md:text-2xl">
        {item.title}
      </h3>
      <p className="body-copy mt-3">{item.description}</p>
      {item.requirements ? (
        <p className="mt-4 border-l-2 border-ochre pl-4 text-sm text-zinc-400">
          <span className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-zinc-400">Requirements</span>
          <br />
          {item.requirements}
        </p>
      ) : null}
    </div>
    <dl className="mono grid grid-cols-2 gap-x-6 gap-y-3 text-xs text-zinc-400 md:grid-cols-1">
      {[
        ["Organisation", item.club],
        ["Location", item.location],
        ["Salary", item.salary],
        ["Closes", item.closes],
      ]
        .filter(([, v]) => v)
        .map(([k, v]) => (
          <div key={k}>
            <dt className="text-[0.56rem] uppercase tracking-[0.22em] text-zinc-400">{k}</dt>
            <dd className="mt-1 text-zinc-300">{v}</dd>
          </div>
        ))}
    </dl>
    <div className="justify-self-start md:justify-self-end">
      {item.apply_url ? (
        <a
          href={item.apply_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`job-register-${item.slug}`}
          className="inline-flex items-center gap-2 border border-ochre/60 px-5 py-3 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-ochre transition-colors hover:bg-ochre hover:text-black"
        >
          Register interest
        </a>
      ) : (
        <span
          data-testid={`job-apply-${item.slug}`}
          className="inline-flex cursor-not-allowed items-center gap-2 border border-white/15 px-5 py-3 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-zinc-500"
          title="Not open for applications yet"
        >
          Not open yet
        </span>
      )}
    </div>
  </div>
);

export default function Opportunities() {
  const [tab, setTab] = useState("training");
  const [filter, setFilter] = useState("all");
  const [trainings, setTrainings] = useState([]);
  const [jobs, setJobs] = useState([]);
  useSeo({
    title: "Training and Jobs",
    description:
      "Coaching badges, physiotherapy, performance analysis and football business courses, plus the role profiles Kenyan football needs — coaching, medical, analysis, club administration and operations. Guidance only: nothing is open for application yet.",
  });

  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/trainings").then((r) => setTrainings(r.data)).catch(() => setError("Could not load courses."));
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setError("Could not load role profiles."));
  }, []);

  const items = tab === "training" ? trainings : jobs;
  const key = tab === "training" ? "track" : "discipline";
  const groups = useMemo(() => ["all", ...new Set(items.map((i) => i[key]).filter(Boolean))], [items, key]);
  const visible = filter === "all" ? items : items.filter((i) => i[key] === filter);

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="opportunities-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Training and jobs</p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <h1 className="display-1 max-w-3xl">Training And Jobs</h1>
            <span
              className="bg-ochre px-3 py-1.5 text-[0.6rem] font-bold uppercase tracking-[0.2em] text-black"
              data-testid="opportunities-coming-soon"
            >
              Coming soon
            </span>
          </div>
          <p className="body-copy mt-6 max-w-3xl">
            The game employs far more people than it selects players — coaches, physios, analysts, groundskeepers, club
            administrators, commercial and media staff. Our modelling puts thousands of coaching and match-official
            qualifications behind the proposed facilities, and those are transferable, internationally recognised skills.
            This page consolidates our own planned pathways with the role profiles and academy programmes published by{" "}
            <span className="text-white">FC Nairobi</span>, <span className="text-white">Crown Crane Park</span> and the{" "}
            <span className="text-white">Kenya Premier League</span>, so you can see the whole picture in one place.
          </p>
          <div
            className="mt-8 border-l-2 border-ochre bg-[#0b0b0b] p-6 md:p-7"
            data-testid="opportunities-guidance-note"
          >
            <p className="overline text-ochre">Guidance only, for now</p>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-300">
              Nothing on this page is open for application yet. The courses are indicative pathways showing what each
              qualification involves and roughly what it costs, and the roles are illustrative job profiles rather than
              live vacancies. Where a role or programme is run by a partner such as FC Nairobi or Crown Crane Park
              Management, the &ldquo;Register interest&rdquo; link takes you to their own careers page and they recruit
              independently of us. We are also <span className="text-white">not currently running player trials</span> — any
              trial or academy information we publish is general guidance to help you understand how selection normally
              works. When registration and recruitment open, it will be announced in{" "}
              <Link to="/updates" className="text-ochre underline decoration-maasai underline-offset-4">
                football development updates
              </Link>
              . If you can help deliver any of it,{" "}
              <Link to="/apply" className="text-ochre underline decoration-maasai underline-offset-4">
                send us a proposal
              </Link>
              .
            </p>
          </div>
          <div className="mono mt-8 flex flex-wrap gap-8 border-t border-white/10 pt-6 text-sm">
            <span data-testid="opportunities-count-training">
              <span className="text-ochre">{trainings.length}</span> planned pathways
            </span>
            <span data-testid="opportunities-count-jobs">
              <span className="text-ochre">{jobs.length}</span> role profiles
            </span>
          </div>
        </Reveal>

        {error ? (
          <p
            className="mt-10 border border-maasai/40 bg-maasai/10 p-5 text-sm text-white"
            data-testid="opportunities-error"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-12 flex flex-wrap items-center gap-2 border-b border-white/10 pb-3" data-testid="opportunities-tabs">
          {TABS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setTab(k);
                setFilter("all");
              }}
              data-testid={`opportunities-tab-${k}`}
              className={`font-display text-lg uppercase tracking-tight transition-colors md:text-2xl ${
                tab === k ? "text-white" : "text-zinc-400 hover:text-zinc-300"
              } ${k === "jobs" ? "ml-6" : ""}`}
            >
              {label}
              {tab === k ? <span className="mt-1 block h-[3px] w-full bg-maasai" /> : null}
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-1" data-testid="opportunities-filters">
          {groups.map((g) => (
            <Chip
              key={g}
              active={filter === g}
              onClick={() => setFilter(g)}
              data-testid={`opportunities-filter-${g.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {g === "all" ? "All" : g}
            </Chip>
          ))}
        </div>

        <div className="mt-8 border-t border-white/20" data-testid="opportunities-list">
          {visible.length === 0 ? (
            <p className="py-10 text-sm text-zinc-500" data-testid="opportunities-empty">
              Nothing listed here yet.
            </p>
          ) : tab === "training" ? (
            visible.map((t, i) => <TrainingRow key={t.id} item={t} index={i} />)
          ) : (
            visible.map((j, i) => <JobRow key={j.id} item={j} index={i} />)
          )}
        </div>
      </div>
    </div>
  );
}
